import React, { useCallback, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { isMigrated360RerunChange } from '@ourbranch/farm-utils';
import { appConfig } from 'brand-config';
import { MIGRATED_CARRY_OVER_MARKER } from 'common/helpers/migrated-carry-over';
import { MigratedRerunConfirmModal } from 'common/components/farm/migrated-rerun-confirm-modal';

/**
 * #37141 (Mutual Services, CFM): whether changing a dwelling's `buildingField` to `newValue` must be confirmed first.
 * Only on a dwelling that came over in a migration (the carry-over marker, stamped on the policy route for the
 * imported term and its renewals), only for the seven fields that re-run 360Value there, and only when the field
 * already held a value: filling in a field that came over blank re-runs nothing, so nothing is asked. Mirrors the
 * policy-mod trigger through the shared isMigrated360RerunChange.
 */
export const needsMigrated360RerunConfirmation = ({ item, buildingField, initialBuilding, newValue }) =>
  Boolean(appConfig?.featureFlags?.confirmMigrated360Rerun) &&
  Boolean(item?.[MIGRATED_CARRY_OVER_MARKER]) &&
  isMigrated360RerunChange({ fieldName: buildingField, oldValue: initialBuilding?.[buildingField], newValue });

/**
 * #37141: the confirmation pop-up for a migrated dwelling's 360Value fields, for agents and staff alike.
 *
 * `guardRerunChange(buildingField, onChange)` wraps a field's onChange. When the change needs confirming (see
 * needsMigrated360RerunConfirmation) the pop-up opens instead of running `onChange`:
 *  - Continue keeps the change, runs `onChange`, and doesn't ask again for that field while the form is open;
 *  - Cancel puts the field back to the value the policy carries, so nothing re-runs.
 * The form fields store the typed value before calling onChange, so Cancel is what reverts it.
 *
 * Render `rerunConfirmModal` once in the risk form.
 */
export const useMigrated360RerunConfirm = ({ item, fieldName, initialBuilding }) => {
  const { setFieldValue } = useFormikContext();
  const confirmedFieldsRef = useRef(new Set());
  const [pendingChange, setPendingChange] = useState(null);

  const guardRerunChange = useCallback(
    (buildingField, onChange) =>
      (newValue, ...rest) => {
        if (
          !confirmedFieldsRef.current.has(buildingField) &&
          needsMigrated360RerunConfirmation({ item, buildingField, initialBuilding, newValue })
        ) {
          setPendingChange({ buildingField, newValue, onChange });
          return;
        }
        onChange?.(newValue, ...rest);
      },
    [item, initialBuilding]
  );

  const onContinue = () => {
    confirmedFieldsRef.current.add(pendingChange.buildingField);
    pendingChange.onChange?.(pendingChange.newValue);
    setPendingChange(null);
  };

  const onCancel = () => {
    const carriedValue = initialBuilding?.[pendingChange.buildingField];
    setFieldValue(`${fieldName}.building.${pendingChange.buildingField}`, carriedValue);
    pendingChange.onChange?.(carriedValue);
    setPendingChange(null);
  };

  const rerunConfirmModal = pendingChange ? (
    <MigratedRerunConfirmModal onContinue={onContinue} onCancel={onCancel} />
  ) : null;

  return { guardRerunChange, rerunConfirmModal };
};
